import { FC, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { ContentCopy } from '@mui/icons-material';
import { Button } from '@mui/material';
import cn from 'classnames';

import useDispatch from '@store/hooks/useDispatch';
import { FileUploaderProps } from '@views/AdminPage/ImportMedications/FileUploader/FileUploader.interface';
import { useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import './style.scss';

const FileUploader: FC<FileUploaderProps> = props => {
  const { className, errors, hasErrors, isLoading, eventType, importHandler, successMessage } =
    props;

  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File>();
  const dispatch = useDispatch();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFile(acceptedFiles[0]);
  }, []);
  const eventBus = useEventBus('adminImportMedications');

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
    },
    disabled: isLoading,
    multiple: false,
    onDrop,
  });

  const handleSubmit = useCallback(() => {
    selectedFile && dispatch(importHandler({ file: selectedFile }));
  }, [dispatch, selectedFile, importHandler]);

  const handleReset = useCallback(() => {
    setSelectedFile(undefined);
  }, []);

  useEffect(() => {
    eventBus.on(eventType, () => {
      handleToast('success', successMessage);
      handleReset();
    });

    return () => eventBus.off(eventType);
  }, [t, eventBus, handleReset, eventType, successMessage]);

  return (
    <div className={cn('file-uploader', { 'file-uploader_disabled': isLoading }, className)}>
      <div className="file-uploader__input" {...getRootProps()}>
        <input {...getInputProps()} />

        <ContentCopy className="file-uploader__icon" />
        <div className="file-uploader__text">
          {selectedFile ? (
            <div className="file-uploader__selected-file">
              <div className="file-uploader__selected-file-name">{selectedFile.name}</div>
              <div className="file-uploader__selected-file-hint">
                {t('admin.importMedicationsDropzoneSelectedHint')}
              </div>
            </div>
          ) : isDragActive ? (
            t('admin.importMedicationsDropzoneDropping')
          ) : (
            t('admin.importMedicationsDropzoneText')
          )}
        </div>
      </div>

      {hasErrors && (
        <div className="file-uploader__errors">
          <div className="file-uploader__errors-title">{t('admin.importMedicationsErrors')}</div>
          <ul className="file-uploader__errors-list">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="file-uploader__controls">
        <Button disabled={isLoading} variant="outlined" onClick={handleReset}>
          {t('common.cancel')}
        </Button>

        <Button disabled={!selectedFile || isLoading} variant="contained" onClick={handleSubmit}>
          {isLoading ? t('admin.importing') : hasErrors ? t('admin.retry') : t('admin.import')}
        </Button>
      </div>
    </div>
  );
};

export default FileUploader;
